import { getAccessToken } from './token-manager'

/**
 * Claims carried in the JWT access token payload. The backend mints these on
 * login/refresh — notably `countryIsoCode`, which is the only reliable
 * client-side source of the user's country (the GraphQL `Query.country` field
 * is non-nullable but can resolve to null, which errors the whole query).
 */
export interface AccessTokenClaims {
  sub?: string
  email?: string
  learnWorldsId?: string
  createdAt?: string
  countryIsoCode?: string
  type?: string
  iat?: number
  exp?: number
}

/** Decode a JWT payload (base64url) without verifying the signature. */
function decodeJwtPayload(token: string): AccessTokenClaims | null {
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64)) as AccessTokenClaims
  } catch {
    return null
  }
}

/** Read the decoded claims from the current access token, if any. */
export function getAccessTokenClaims(): AccessTokenClaims | null {
  const token = getAccessToken()
  return token ? decodeJwtPayload(token) : null
}

/** The user's country ISO code (e.g. "US") from the access token, or null. */
export function getCountryIsoCode(): string | null {
  return getAccessTokenClaims()?.countryIsoCode ?? null
}

/**
 * Unincorporated US territories. `countryIsoCode` originates from Cloudflare's
 * CF-IPCountry header and is stored/forwarded verbatim, and CF-IPCountry reports
 * these as their own ISO 3166-1 alpha-2 codes — never 'US'. So any check that
 * means "the United States" must name them explicitly or it silently excludes
 * every user in Puerto Rico, Guam, the US Virgin Islands, American Samoa and the
 * Northern Mariana Islands.
 */
const US_TERRITORIES = ['PR', 'GU', 'VI', 'AS', 'MP']

/** The US and everything under its jurisdiction. */
const US_JURISDICTION_COUNTRIES = ['US', ...US_TERRITORIES]

/**
 * Countries where consumer-cancellation compliance rules require the simplified,
 * friction-free cancel experience: skip the retention/discount step, show a plain
 * "Cancel" button, and go straight to confirm-cancel. Originally US-only; now also
 * Ukraine (UA), Cyprus (CY), and Belgium (BE).
 */
export const COMPLIANCE_SENSITIVE_COUNTRIES = [
  ...US_JURISDICTION_COUNTRIES,
  'UA',
  'CY',
  'BE',
]

/**
 * Whether the current user is in a compliance-sensitive country and must receive
 * the simplified cancellation flow (and the behaviours that were previously gated
 * to US only): a single-step confirm→success cancel (no funnel, no email-confirm
 * even for Non-Visa), and the "Subscription info" copy.
 */
export function isComplianceSensitive(): boolean {
  const country = getCountryIsoCode()
  return country !== null && COMPLIANCE_SENSITIVE_COUNTRIES.includes(country)
}

/**
 * US jurisdiction only. Drives US-specific COPY — the "Subscription info" wording
 * (instead of "Membership info" / "Go back to membership info") — which is distinct
 * from the simplified cancel FLOW above: that flow also covers UA/CY/BE
 * (isComplianceSensitive), but the "subscription" terminology is US-only, and
 * UA/CY/BE keep "membership".
 */
export function isUsUser(): boolean {
  const country = getCountryIsoCode()
  return country !== null && US_JURISDICTION_COUNTRIES.includes(country)
}
